import { env } from "../../config/Env";
import { STORAGE_KEYS } from "../../config/Constants";
import { generateMasterM2MToken } from "../../core/crypto/RsaSigner";
import { encodeBase64 } from "../../utils/base64";

/**
 * Cliente HTTP personalizado basado en Fetch API nativo.
 * Maneja automáticamente:
 * 1. Generación e inyección del Bearer Token M2M (RS256).
 * 2. Inyección del token de sesión del usuario humano (X-User-Session).
 * 3. Codificación de payloads JSON a Base64 ({ data: "base64..." }).
 * 4. Parseo de respuestas y normalización de errores HTTP.
 */
async function http(endpoint, options = {}) {
  const { body, headers = {}, customScopes = null, queryParams = null, ...customOptions } = options;

  // 1. Obtener Token M2M firmado por RS256 mediante 'jose'
  const m2mToken = await generateMasterM2MToken(customScopes);

  // 2. Construir Headers por defecto
  const requestHeaders = {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "Authorization": `Bearer ${m2mToken}`,
    ...headers,
  };

  // Adjuntar token de sesión del usuario humano si existe en LocalStorage
  const userToken = localStorage.getItem(STORAGE_KEYS.USER_TOKEN);
  if (userToken) {
    requestHeaders["X-User-Session"] = userToken;
  }

  // 3. Procesar Body (Codificar a Base64 bajo { data: "..." } si es un objeto JS)
  let requestBody = body;
  if (body && typeof body === "object" && !(body instanceof FormData)) {
    // Si la data no viene empaquetada previamente bajo la propiedad 'data'
    const payloadToEncode = body.data ? body : { data: encodeBase64(body) };
    requestBody = JSON.stringify(payloadToEncode);
  }

  // 4. Configurar la petición Fetch
  const config = {
    method: options.method || (body ? "POST" : "GET"),
    headers: requestHeaders,
    body: requestBody,
    ...customOptions,
  };

  // Construir la URL completa
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  const params = new URLSearchParams(queryParams).toString()

  const fullUrl = `${env.API_URL}${cleanEndpoint}?${params}`; 

  try {
    const response = await fetch(fullUrl, config);
    let responseData = null;

    // Intentar parsear la respuesta como JSON
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    // 5. Manejo de errores HTTP (Status fuera del rango 200-299)
    if (!response.ok) {
      const errorObject = {
        success: false,
        status: response.status,
        message:
          responseData?.message ||
          `Error HTTP ${response.status}: ${response.statusText}`,
        data: responseData?.data || null,
      };

      // Manejo de token de usuario vencido (401 / 403)
      if (response.status === 401) {
        console.warn("🔒 [AUTH EXPIRED]: Sesión expirada o no autorizada.");
      }

      return errorObject
    }

    return responseData;
  } catch (error) {
    // Error de Red / CORS / Timeout
    console.error("❌ [FETCH CLIENT ERROR]:", error);

    const networkError = {
      success: false,
      status: error.status || 500,
      message: error.message || "Error de conexión o bloqueo de red.",
      data: null,
    };

    return networkError
  }
}

/**
 * Métodos HTTP expuestos
 */
export const apiClient = {
  get: (endpoint, options = {}) =>
    http(endpoint, { ...options, method: "GET" }),

  post: (endpoint, body, options = {}) =>
    http(endpoint, { ...options, method: "POST", body }),

  put: (endpoint, body, options = {}) =>
    http(endpoint, { ...options, method: "PUT", body }),

  patch: (endpoint, body, options = {}) =>
    http(endpoint, { ...options, method: "PATCH", body }),

  delete: (endpoint, options = {}) =>
    http(endpoint, { ...options, method: "DELETE" }),
};